<script>
	import { onDestroy } from "svelte";
	import Button, { Label } from "@smui/button";
	import { Icon } from "@smui/common";
	import api from "../api";
	import InternalBadge from "./graphics/components/InternalBadge.svelte";
	import {
		graphics,
		embedInfo,
		selectedOrganization,
		selectedOrganizationObj,
		selectedLanguage,
		selectedProject,
		organizationProjectSettings,
		fetchOrganizationProjectSettings,
	} from "../stores";

	let colorScheme = "light";
	let selectedGraphicIds = [];
	let lastLoadedSettingsId = null;
	let copied = false;

	let deviceMode = "responsive";
	let previewWidth = 375;
	let previewHeight = 667;
	let deviceFrameEl;
	let iframeEl;
	let activeResizeCleanup = null;
	let copyTimeout = null;

	function setMobile() {
		deviceMode = "preset";
		previewWidth = 375;
		previewHeight = 667;
	}

	function setDesktop() {
		deviceMode = "responsive";
	}

	function onDimensionInput() {
		previewWidth = Math.max(320, Math.min(previewWidth || 320, 3840));
		previewHeight = Math.max(200, Math.min(previewHeight || 200, 2160));
	}

	function onResizeStart(e) {
		e.preventDefault();
		const startX = e.clientX;
		const startY = e.clientY;
		const startW = previewWidth;
		const startH = previewHeight;

		function onMouseMove(ev) {
			previewWidth = Math.max(320, startW + (ev.clientX - startX));
			previewHeight = Math.max(200, startH + (ev.clientY - startY));
		}

		function onMouseUp() {
			window.removeEventListener("mousemove", onMouseMove);
			window.removeEventListener("mouseup", onMouseUp);
			activeResizeCleanup = null;
		}

		if (activeResizeCleanup) activeResizeCleanup();

		window.addEventListener("mousemove", onMouseMove);
		window.addEventListener("mouseup", onMouseUp);
		activeResizeCleanup = () => {
			window.removeEventListener("mousemove", onMouseMove);
			window.removeEventListener("mouseup", onMouseUp);
		};
	}

	// Load initial selection from organizationProjectSettings
	$: if ($organizationProjectSettings && $organizationProjectSettings.id !== lastLoadedSettingsId) {
		selectedGraphicIds = $organizationProjectSettings.preview_selected_graphics || [];
		lastLoadedSettingsId = $organizationProjectSettings.id;
	}

	$: org = $selectedOrganizationObj;

	$: previewUrl = $organizationProjectSettings?.preview_url;

	// Build the iframe URL using the absolute backend URL from the API
	$: iframeSrc = previewUrl
		? `${previewUrl}?lang=${$selectedLanguage || "en"}&scheme=${colorScheme}`
		: "";

	$: publicLink = previewUrl
		? `${previewUrl}?lang=${$selectedLanguage || "en"}`
		: "";

	$: selectedGraphics = ($graphics || []).filter((g) =>
		selectedGraphicIds.includes(g.graphic_id)
	);

	let persistTimer = null;

	function toggleGraphic(graphicId) {
		if (selectedGraphicIds.includes(graphicId)) {
			selectedGraphicIds = selectedGraphicIds.filter((id) => id !== graphicId);
		} else {
			selectedGraphicIds = [...selectedGraphicIds, graphicId];
		}
		debouncedPersist();
	}

	function debouncedPersist() {
		if (persistTimer) clearTimeout(persistTimer);
		persistTimer = setTimeout(() => {
			persistTimer = null;
			persistSelection();
		}, 300);
	}

	async function persistSelection() {
		const settingsId = `${$selectedOrganization}__${$selectedProject}`;
		await api.patch(`organisationProjectSettings/${settingsId}/`, {
			preview_selected_graphics: selectedGraphicIds,
		});
		reloadIframe();
	}

	let iframeLoading = false;

	$: if (iframeSrc) {
		iframeLoading = true;
	}

	function reloadIframe() {
		if (iframeEl) {
			iframeLoading = true;
			iframeEl.src = iframeSrc;
		}
	}

	function onIframeLoad() {
		iframeLoading = false;
	}

	async function copyPreviewLink() {
		if (!publicLink) return;
		try {
			await navigator.clipboard.writeText(publicLink);
			copied = true;
			if (copyTimeout) clearTimeout(copyTimeout);
			copyTimeout = setTimeout(() => {
				copied = false;
				copyTimeout = null;
			}, 2000);
		} catch {
			copied = false;
		}
	}

	onDestroy(() => {
		if (activeResizeCleanup) activeResizeCleanup();
		if (persistTimer) clearTimeout(persistTimer);
		if (copyTimeout) clearTimeout(copyTimeout);
	});
</script>

<div class="article-preview">
	<div class="settings-bar">
		<div class="settings-content">
			<div class="graphics-list">
				<span class="settings-label">Graphics</span>
				{#each $graphics as graphic}
					<div class="graphic-card" class:selected={selectedGraphicIds.includes(graphic.graphic_id)} on:click={() => toggleGraphic(graphic.graphic_id)}>
						<input type="checkbox" checked={selectedGraphicIds.includes(graphic.graphic_id)} tabindex="-1" />
						{graphic.meta_title || graphic.graphic_id}
						{#if graphic.internal_only}<InternalBadge />{/if}
						{#if graphic.instances?.some(i => i.organisation === $selectedOrganization)} <span class="used-badge">used</span>{/if}
					</div>
				{/each}
				{#if $graphics.length === 0}
					<span class="no-graphics">No graphics in this project</span>
				{/if}
			</div>
		</div>

		<div class="settings-content settings-row">
			<div class="settings-left">
				{#if org?.darkmode_enabled}
					<div class="settings-section">
						<span class="settings-label">Mode</span>
						<div class="mode-toggle">
							<Button
								on:click={() => (colorScheme = "light")}
								variant={colorScheme === "light" ? "unelevated" : "outlined"}
								color="primary"
							>
								<Label>Light</Label>
							</Button>
							<Button
								on:click={() => (colorScheme = "dark")}
								variant={colorScheme === "dark" ? "unelevated" : "outlined"}
								color="primary"
							>
								<Label>Dark</Label>
							</Button>
						</div>
					</div>
				{/if}

				<div class="settings-section">
					<span class="settings-label">Device</span>
					<div class="device-buttons">
						<button
							class="device-btn"
							class:active={deviceMode === "responsive"}
							on:click={setDesktop}
						>
							<Icon class="material-icons">monitor</Icon>
						</button>
						<button
							class="device-btn"
							class:active={deviceMode === "preset"}
							on:click={setMobile}
						>
							<Icon class="material-icons">phone_android</Icon>
						</button>
					</div>
					{#if deviceMode === "preset"}
						<div class="dimension-inputs">
							<span class="dim-label">W</span>
							<input
								type="number"
								class="dim-input"
								bind:value={previewWidth}
								on:change={onDimensionInput}
								min="320"
								max="3840"
							/>
							<span class="dimension-separator">&times;</span>
							<span class="dim-label">H</span>
							<input
								type="number"
								class="dim-input"
								bind:value={previewHeight}
								on:change={onDimensionInput}
								min="200"
								max="2160"
							/>
						</div>
					{/if}
				</div>
			</div>

			<div class="settings-right">
				<Button
					on:click={copyPreviewLink}
					variant="outlined"
					color="primary"
					disabled={!publicLink}
				>
					<Label>{copied ? "Copied!" : "Copy preview link"}</Label>
				</Button>
			</div>
		</div>
	</div>

	<div class="preview-viewport" class:preview-viewport--preset={deviceMode === "preset"}>
		<div
			class="article-frame"
			class:article-frame--preset={deviceMode === "preset"}
			bind:this={deviceFrameEl}
			style:width={deviceMode === "preset" ? previewWidth + "px" : null}
			style:height={deviceMode === "preset" ? previewHeight + "px" : null}
		>
			{#if iframeSrc}
				<iframe
					bind:this={iframeEl}
					src={iframeSrc}
					title="Article Preview"
					frameborder="0"
					on:load={onIframeLoad}
				></iframe>
				{#if iframeLoading}
					<div class="loading-overlay">
						<span class="loading-spinner"></span>
					</div>
				{/if}
			{:else}
				<p class="no-preview">No preview available</p>
			{/if}
			{#if deviceMode === "preset"}
				<div class="resize-handle" on:mousedown={onResizeStart}></div>
			{/if}
		</div>
	</div>
</div>

<style lang="scss">
	.article-preview {
		display: flex;
		flex-direction: column;
		height: 100%;
		flex: 1;
		min-width: 0;
	}

	.settings-bar {
		position: sticky;
		top: 0;
		z-index: 10;
		background: #f5f5f5;
		border-bottom: 1px solid #ddd;
		padding: 8px 16px;
		flex-shrink: 0;
	}

	.settings-content {
		display: flex;
		align-items: center;
		gap: 24px;
		flex-wrap: wrap;
	}

	.settings-row {
		padding-top: 8px;
		justify-content: space-between;
	}

	.settings-section {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.settings-left {
		display: flex;
		align-items: center;
		gap: 24px;
	}

	.settings-right {
		display: flex;
		align-items: center;
		margin-left: auto;
	}

	.settings-label {
		font-weight: 500;
		font-size: 0.85rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: #666;
		white-space: nowrap;
		margin-right: 4px;
	}

	.graphics-list {
		display: flex;
		flex-wrap: nowrap;
		overflow-x: auto;
		gap: 6px;
		align-items: stretch;
		scrollbar-width: thin;
		padding: 8px 0;
	}

	.graphic-card {
		padding: 12px 8px;
		border: 1px solid #ccc;
		border-radius: 4px;
		background: #fff;
		cursor: pointer;
		flex-shrink: 0;
		font-family: inherit;
		font-size: 0.7rem;
		line-height: 1.5;
		max-width: 100px;
		text-align: left;

		input[type="checkbox"] {
			width: 14px;
			height: 14px;
			margin: 0 2px 0 0;
			vertical-align: middle;
			pointer-events: none;
		}

		&.selected {
			border-color: #1976d2;
			background: #e3f2fd;
		}
	}

	.used-badge {
		font-size: inherit;
		background: #c8e6c9;
		color: #2e7d32;
		padding: 0 4px;
		border-radius: 3px;
		display: inline-block;
		vertical-align: middle;
		line-height: 1.2;
		white-space: nowrap;
	}

	.no-graphics {
		font-size: 0.85rem;
		color: #999;
		font-style: italic;
	}

	.mode-toggle {
		display: flex;
		gap: 4px;
	}

	.device-buttons {
		display: flex;
		gap: 2px;
	}

	.device-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		background: none;
		border: none;
		cursor: pointer;
		padding: 4px;
		border-radius: 4px;
		color: #666;

		&:hover {
			background: rgba(0, 0, 0, 0.08);
		}

		&.active {
			color: #1976d2;
		}

		.material-icons {
			font-size: 20px;
		}
	}

	.dimension-inputs {
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.dim-label {
		font-size: 0.75rem;
		font-weight: 500;
		color: #666;
	}

	.dim-input {
		width: 64px;
		padding: 4px 6px;
		border: 1px solid #ccc;
		border-radius: 3px;
		font-size: 0.85rem;
		font-family: inherit;

		&:focus {
			outline: none;
			border-color: #1976d2;
		}
	}

	.dimension-separator {
		font-size: 0.85rem;
		color: #666;
	}

	.preview-viewport {
		flex: 1;
		display: flex;
		justify-content: center;
		align-items: flex-start;
		overflow: auto;
		padding: 0;
		background: #fff;
	}

	.preview-viewport--preset {
		padding: 24px;
		background: #e0e0e0;
	}

	.article-frame {
		position: relative;
		width: 100%;
		height: 100%;

		iframe {
			width: 100%;
			height: 100%;
			border: none;
		}
	}

	.article-frame--preset {
		border: 1px solid #bbb;
		border-radius: 4px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
		flex-shrink: 0;
	}

	.no-preview {
		text-align: center;
		color: #999;
		padding: 48px;
	}

	.loading-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(255, 255, 255, 0.7);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 5;
	}

	.loading-spinner {
		width: 32px;
		height: 32px;
		border: 3px solid #ddd;
		border-top-color: #1976d2;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
	}

	@keyframes spin {
		to { transform: rotate(360deg); }
	}

	.resize-handle {
		position: absolute;
		bottom: 0;
		right: 0;
		width: 16px;
		height: 16px;
		cursor: nwse-resize;

		&::before {
			content: "";
			position: absolute;
			bottom: 3px;
			right: 3px;
			width: 8px;
			height: 8px;
			border-right: 2px solid #999;
			border-bottom: 2px solid #999;
		}

		&::after {
			content: "";
			position: absolute;
			bottom: 3px;
			right: 3px;
			width: 4px;
			height: 4px;
			border-right: 2px solid #999;
			border-bottom: 2px solid #999;
		}
	}
</style>
